import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { BASE } from '../api/client';
import { SceneReferences } from '../components/SceneReferences';
import {
  useApproveKeyframe,
  useApproveShot,
  useAddSceneSeriesScene,
  useBuildFinal,
  useBuildRhythm,
  useGenerateKeyframe,
  useGenerateShot,
  useRemoveSceneSeriesScene,
  useSceneSeries,
  useSceneSeriesAudio,
  useSceneSeriesLook,
  useSetSceneSeriesStep,
  useUpdateSceneSeriesScenes,
  type SceneSeriesScene,
  type SceneSeriesState,
} from '../hooks/useApi';

/**
 * Serie de escenas — wizard de 6 pasos.
 *
 * El orden de los pasos es el orden de costo creciente, y hay tres puertas:
 * el keyframe se aprueba antes de animar, el ritmo se valida antes de pagar
 * video, y el plano se aprueba antes de entrar en la entrega. Los pasos que
 * todavía no se pueden abrir vienen marcados por el backend en `reachableSteps`
 * — la interfaz no decide eso por su cuenta.
 */

const STEPS: Array<{ key: string; label: string; hint: string }> = [
  { key: 'partes', label: '1 · Partes', hint: 'Desglosar la escena. No cuesta.' },
  { key: 'keyframes', label: '2 · Keyframes', hint: 'Una imagen por toma. Acá se corrige el encuadre.' },
  { key: 'audio', label: '3 · Voz y música', hint: 'Qué se dice y qué suena.' },
  { key: 'ritmo', label: '4 · Ritmo', hint: 'Ver cómo respira. No cuesta.' },
  { key: 'planos', label: '5 · Planos', hint: 'Animar lo aprobado. Es lo caro.' },
  { key: 'entrega', label: '6 · Entrega', hint: 'Bajar sueltos o unidos.' },
];

const STYLES = [
  { key: 'cinematografico', label: 'Cinematográfico' },
  { key: '3d_dramatico', label: '3D con dramatismo' },
];

const PACINGS = [
  { key: 'quieto', label: 'Quieto' },
  { key: 'sutil', label: 'Sutil' },
  { key: 'camara', label: 'Con movimiento de cámara' },
];

export function SceneSeriesPage() {
  const { seriesId } = useParams<{ seriesId: string }>();
  const id = seriesId ?? null;
  const { data, isLoading, error } = useSceneSeries(id);
  const setStep = useSetSceneSeriesStep(id);

  if (isLoading) return <div className="p-6 text-neutral-400">Cargando la serie…</div>;
  if (error || !data) return <div className="p-6 text-red-400">No pude cargar la serie.</div>;

  const step = data.job.step;

  return (
    <div className="flex flex-col gap-4 p-6">
      <Header data={data} seriesId={id!} />

      <nav className="flex flex-wrap gap-2">
        {STEPS.map((s) => {
          const reachable = data.reachableSteps.includes(s.key);
          const active = s.key === step;
          return (
            <button
              key={s.key}
              disabled={!reachable}
              title={reachable ? s.hint : 'Falta aprobar lo del paso anterior'}
              onClick={() => setStep.mutate(s.key)}
              className={[
                'rounded px-3 py-1.5 text-sm transition',
                active ? 'bg-emerald-600 text-white' : 'bg-neutral-800 text-neutral-300',
                reachable ? 'hover:bg-neutral-700' : 'cursor-not-allowed opacity-40',
              ].join(' ')}
            >
              {s.label}
            </button>
          );
        })}
      </nav>

      <p className="text-sm text-neutral-500">{STEPS.find((s) => s.key === step)?.hint}</p>

      {step === 'partes' && <PartesStep data={data} seriesId={id!} />}
      {step === 'keyframes' && <KeyframesStep data={data} seriesId={id!} />}
      {step === 'audio' && <AudioStep data={data} seriesId={id!} />}
      {step === 'ritmo' && <RitmoStep data={data} seriesId={id!} />}
      {step === 'planos' && <PlanosStep data={data} seriesId={id!} />}
      {step === 'entrega' && <EntregaStep data={data} seriesId={id!} />}
    </div>
  );
}

// ── Encabezado: look de la serie + la plata ──────────────────────────────────

function Header({ data, seriesId }: { data: SceneSeriesState; seriesId: string }) {
  const look = useSceneSeriesLook(seriesId);
  const { estimate, job } = data;
  const [openCost, setOpenCost] = useState(false);

  return (
    <header className="flex flex-col gap-3 rounded-lg bg-neutral-900 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-neutral-100">{job.title}</h1>
        <div className="flex items-center gap-2">
          <select
            value={job.style}
            onChange={(e) => look.mutate({ style: e.target.value })}
            className="rounded bg-neutral-800 px-2 py-1 text-sm text-neutral-200"
          >
            {STYLES.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <select
            value={job.pacing}
            onChange={(e) => look.mutate({ pacing: e.target.value })}
            className="rounded bg-neutral-800 px-2 py-1 text-sm text-neutral-200"
          >
            {PACINGS.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Cambiar estilo o ritmo no borra nada aprobado: solo re-propone prompts. */}
      <p className="text-xs text-neutral-500">
        Cambiar el estilo o el ritmo no borra lo que ya aprobaste: se aplican a lo que generes de ahora en más.
      </p>

      <button
        onClick={() => setOpenCost((v) => !v)}
        className="self-start text-left text-sm text-emerald-400 hover:underline"
      >
        Estimado: US${estimate.totalUsd.toFixed(2)}
        {estimate.partial && ' (hay un modelo sin precio conocido)'}
        {estimate.naiveUsd != null && (
          <span className="text-neutral-500">
            {' '}· yendo directo al video final serían ~US${estimate.naiveUsd.toFixed(2)}
          </span>
        )}
      </button>

      {openCost && (
        <ul className="flex flex-col gap-1 rounded bg-neutral-950 p-3 text-xs">
          {estimate.lines.map((l, i) => (
            <li key={i} className="flex justify-between gap-4">
              <span className="text-neutral-300">
                {l.label} <span className="text-neutral-600">— {l.detail}</span>
              </span>
              <span className={l.usd === 0 ? 'text-emerald-500' : 'text-neutral-200'}>
                {l.usd == null ? '—' : l.usd === 0 ? 'no cuesta' : `US$${l.usd.toFixed(2)}`}
              </span>
            </li>
          ))}
        </ul>
      )}

      {job.costUsd && (
        <p className="text-xs text-neutral-400">Gastado hasta ahora: US${Number(job.costUsd).toFixed(2)}</p>
      )}
    </header>
  );
}

// ── Paso 1: partes ───────────────────────────────────────────────────────────

function PartesStep({ data, seriesId }: { data: SceneSeriesState; seriesId: string }) {
  const update = useUpdateSceneSeriesScenes(seriesId);
  const add = useAddSceneSeriesScene(seriesId);
  const remove = useRemoveSceneSeriesScene(seriesId);
  const [draft, setDraft] = useState<Record<string, Partial<SceneSeriesScene>>>({});

  const total = useMemo(
    () => data.scenes.reduce((a, s) => a + (draft[s.id]?.durationSec ?? s.durationSec), 0),
    [data.scenes, draft],
  );

  const save = () => {
    const updates = Object.entries(draft).map(([id, d]) => ({ id, ...d }));
    if (updates.length) update.mutate(updates, { onSuccess: () => setDraft({}) });
  };

  return (
    <section className="flex flex-col gap-3">
      <table className="w-full text-sm">
        <thead className="text-left text-neutral-500">
          <tr>
            <th className="p-2">Toma</th>
            <th className="p-2">Qué pasa</th>
            <th className="p-2 w-28">Duración</th>
            <th className="p-2 w-10" />
          </tr>
        </thead>
        <tbody>
          {data.scenes.map((s, i) => (
            <tr key={s.id} className="border-t border-neutral-800">
              <td className="p-2">
                <input
                  defaultValue={s.title}
                  onChange={(e) => setDraft((d) => ({ ...d, [s.id]: { ...d[s.id], title: e.target.value } }))}
                  className="w-full rounded bg-neutral-800 px-2 py-1 text-neutral-100"
                />
              </td>
              <td className="p-2">
                <input
                  defaultValue={s.description}
                  onChange={(e) => setDraft((d) => ({ ...d, [s.id]: { ...d[s.id], description: e.target.value } }))}
                  className="w-full rounded bg-neutral-800 px-2 py-1 text-neutral-100"
                />
              </td>
              <td className="p-2">
                <input
                  type="number"
                  min={2}
                  max={30}
                  defaultValue={s.durationSec}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, [s.id]: { ...d[s.id], durationSec: Number(e.target.value) } }))
                  }
                  className="w-20 rounded bg-neutral-800 px-2 py-1 text-neutral-100"
                />
                <span className="ml-1 text-neutral-500">s</span>
              </td>
              <td className="p-2">
                <button
                  onClick={() => remove.mutate(s.id)}
                  className="text-neutral-500 hover:text-red-400"
                  title={`Sacar la toma ${i + 1}`}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center gap-3">
        <button
          onClick={() => add.mutate({ title: `Toma ${data.scenes.length + 1}`, description: '', durationSec: 5 })}
          className="rounded bg-neutral-800 px-3 py-1.5 text-sm text-neutral-200 hover:bg-neutral-700"
        >
          + Agregar toma
        </button>
        <button
          onClick={save}
          disabled={!Object.keys(draft).length || update.isPending}
          className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
        >
          Guardar cambios
        </button>
        <span className="text-sm text-neutral-500">
          {data.scenes.length} tomas · {total}s en total
        </span>
      </div>
    </section>
  );
}

// ── Paso 2: keyframes ────────────────────────────────────────────────────────

function KeyframesStep({ data, seriesId }: { data: SceneSeriesState; seriesId: string }) {
  const generate = useGenerateKeyframe(seriesId);
  const approve = useApproveKeyframe(seriesId);
  const update = useUpdateSceneSeriesScenes(seriesId);
  const [prompts, setPrompts] = useState<Record<string, string>>({});
  const approved = data.scenes.filter((s) => s.keyframeApproved).length;

  return (
    <section className="flex flex-col gap-3">
      <p className="text-sm text-neutral-400">
        {approved} de {data.scenes.length} aprobados. Hasta que estén todos no se puede seguir.
      </p>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.scenes.map((s) => (
          <article
            key={s.id}
            className={[
              'flex flex-col gap-2 rounded-lg border p-3',
              s.keyframeApproved ? 'border-emerald-700 bg-emerald-950/20' : 'border-neutral-800 bg-neutral-900',
            ].join(' ')}
          >
            <h3 className="text-sm font-medium text-neutral-200">{s.title}</h3>

            {s.keyframeAssetId ? (
              <img
                src={`${BASE}/assets/${s.keyframeAssetId}/thumb`}
                alt={s.title}
                className="aspect-video w-full rounded object-cover"
              />
            ) : (
              <div className="flex aspect-video w-full items-center justify-center rounded bg-neutral-950 text-xs text-neutral-600">
                sin imagen todavía
              </div>
            )}

            {/* La referencia es de ESTA toma. Cambiarla no borra el keyframe ya
                generado: se nota recién cuando se regenera. */}
            <SceneReferences
              value={s.referenceAssetIds ?? []}
              onChange={(ids) => update.mutate([{ id: s.id, referenceAssetIds: ids }])}
            />

            <textarea
              rows={2}
              placeholder="Corrección: 'sacale la silla', 'más cerrado'…"
              value={prompts[s.id] ?? ''}
              onChange={(e) => setPrompts((p) => ({ ...p, [s.id]: e.target.value }))}
              className="rounded bg-neutral-800 px-2 py-1 text-xs text-neutral-100"
            />

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => generate.mutate({ sceneId: s.id, prompt: prompts[s.id] || undefined })}
                disabled={generate.isPending}
                className="rounded bg-neutral-700 px-2 py-1 text-xs text-neutral-100 hover:bg-neutral-600 disabled:opacity-40"
              >
                {s.keyframeAssetId ? 'Rehacer' : 'Generar'} (boceto)
              </button>
              <button
                onClick={() => generate.mutate({ sceneId: s.id, prompt: prompts[s.id] || undefined, quality: 'final' })}
                disabled={generate.isPending}
                className="rounded bg-neutral-800 px-2 py-1 text-xs text-neutral-300 hover:bg-neutral-700 disabled:opacity-40"
                title="Cuesta unas 4 veces más que el boceto. Usalo cuando el encuadre ya esté decidido."
              >
                En calidad final
              </button>
              <button
                onClick={() => approve.mutate({ sceneId: s.id, approved: !s.keyframeApproved })}
                disabled={!s.keyframeAssetId}
                className={[
                  'ml-auto rounded px-2 py-1 text-xs disabled:opacity-40',
                  s.keyframeApproved ? 'bg-emerald-700 text-white' : 'bg-emerald-600/20 text-emerald-300',
                ].join(' ')}
              >
                {s.keyframeApproved ? '✓ Aprobado' : 'Aprobar'}
              </button>
            </div>

            {s.sourceVideoAssetId && (
              <p className="text-xs text-neutral-600">
                Esta imagen salió del primer cuadro del video que trajiste.
              </p>
            )}

            {/* Regenerar tira el plano de esa toma: hay que decirlo antes, no después. */}
            {s.videoAssetId && (
              <p className="text-xs text-amber-500">
                Si rehacés este keyframe, se descarta el plano ya generado de esta toma.
              </p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

// ── Paso 3: voz y música ─────────────────────────────────────────────────────

function AudioStep({ data, seriesId }: { data: SceneSeriesState; seriesId: string }) {
  const audio = useSceneSeriesAudio(seriesId);
  const [texts, setTexts] = useState<Record<string, string>>({});

  return (
    <section className="flex flex-col gap-4">
      <label className="flex items-center gap-2 text-sm text-neutral-200">
        <input
          type="checkbox"
          checked={data.job.narrationEnabled}
          onChange={(e) => audio.mutate({ narrationEnabled: e.target.checked })}
        />
        Lleva locución
      </label>

      {data.job.narrationEnabled && (
        <div className="flex flex-col gap-2">
          {data.scenes.map((s) => (
            <div key={s.id} className="flex items-start gap-3">
              <span className="w-32 shrink-0 pt-1 text-sm text-neutral-400">{s.title}</span>
              <textarea
                rows={2}
                defaultValue={s.narrationText ?? ''}
                placeholder="Qué se dice en esta toma"
                onChange={(e) => setTexts((t) => ({ ...t, [s.id]: e.target.value }))}
                className="flex-1 rounded bg-neutral-800 px-2 py-1 text-sm text-neutral-100"
              />
            </div>
          ))}
          <button
            onClick={() =>
              audio.mutate({
                narrationByScene: Object.entries(texts).map(([id, narrationText]) => ({ id, narrationText })),
              })
            }
            disabled={!Object.keys(texts).length}
            className="self-start rounded bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
          >
            Guardar textos
          </button>
        </div>
      )}

      <p className="text-xs text-neutral-500">
        Cambiar la voz o la música no toca ninguna imagen ni video: solo hay que rehacer el armado.
      </p>
    </section>
  );
}

// ── Paso 4: ritmo ────────────────────────────────────────────────────────────

function RitmoStep({ data, seriesId }: { data: SceneSeriesState; seriesId: string }) {
  const build = useBuildRhythm(seriesId);
  const asset = data.job.rhythmVideoAssetId;

  return (
    <section className="flex flex-col gap-3">
      <p className="text-sm text-neutral-400">
        Los keyframes aprobados montados con su duración, más la voz y la música. No genera video con IA:
        ajustá tiempos y volvé a verlo las veces que quieras, no cuesta nada.
      </p>

      <button
        onClick={() => build.mutate(undefined)}
        disabled={build.isPending}
        className="self-start rounded bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
      >
        {build.isPending ? 'Armando…' : asset ? 'Rehacer el video de ritmo' : 'Armar el video de ritmo'}
      </button>

      {asset && (
        <video src={`${BASE}/assets/${asset}/proxy`} controls className="max-w-3xl rounded" />
      )}
      {!asset && !build.isPending && (
        <p className="text-xs text-neutral-600">
          Si cambiaste una duración o un keyframe, el ritmo anterior se descartó: volvé a armarlo.
        </p>
      )}
    </section>
  );
}

// ── Paso 5: planos ───────────────────────────────────────────────────────────

function PlanosStep({ data, seriesId }: { data: SceneSeriesState; seriesId: string }) {
  const generate = useGenerateShot(seriesId);
  const approve = useApproveShot(seriesId);
  const [prompts, setPrompts] = useState<Record<string, string>>({});
  const approved = data.scenes.filter((s) => s.videoApproved).length;

  return (
    <section className="flex flex-col gap-3">
      <p className="text-sm text-neutral-400">
        {approved} de {data.scenes.length} planos aprobados. Este es el paso caro: el borrador sale la mitad
        que la entrega.
      </p>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.scenes.map((s) => (
          <article
            key={s.id}
            className={[
              'flex flex-col gap-2 rounded-lg border p-3',
              s.videoApproved ? 'border-emerald-700 bg-emerald-950/20' : 'border-neutral-800 bg-neutral-900',
            ].join(' ')}
          >
            <h3 className="text-sm font-medium text-neutral-200">
              {s.title} <span className="text-neutral-500">· {s.durationSec}s</span>
            </h3>

            {s.videoAssetId ? (
              <video src={`${BASE}/assets/${s.videoAssetId}/proxy`} controls className="w-full rounded" />
            ) : (
              <img
                src={`${BASE}/assets/${s.keyframeAssetId}/thumb`}
                alt={s.title}
                className="aspect-video w-full rounded object-cover opacity-60"
              />
            )}

            <textarea
              rows={2}
              placeholder="Movimiento: 'que la cámara entre despacio'…"
              value={prompts[s.id] ?? ''}
              onChange={(e) => setPrompts((p) => ({ ...p, [s.id]: e.target.value }))}
              className="rounded bg-neutral-800 px-2 py-1 text-xs text-neutral-100"
            />

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => generate.mutate({ sceneId: s.id, prompt: prompts[s.id] || undefined })}
                disabled={generate.isPending}
                className="rounded bg-neutral-700 px-2 py-1 text-xs text-neutral-100 hover:bg-neutral-600 disabled:opacity-40"
              >
                Borrador
              </button>
              <button
                onClick={() => generate.mutate({ sceneId: s.id, prompt: prompts[s.id] || undefined, quality: 'final' })}
                disabled={generate.isPending}
                className="rounded bg-neutral-800 px-2 py-1 text-xs text-neutral-300 hover:bg-neutral-700 disabled:opacity-40"
              >
                Entrega
              </button>
              <button
                onClick={() => approve.mutate({ sceneId: s.id, approved: !s.videoApproved })}
                disabled={!s.videoAssetId}
                className={[
                  'ml-auto rounded px-2 py-1 text-xs disabled:opacity-40',
                  s.videoApproved ? 'bg-emerald-700 text-white' : 'bg-emerald-600/20 text-emerald-300',
                ].join(' ')}
              >
                {s.videoApproved ? '✓ Aprobado' : 'Aprobar'}
              </button>
            </div>

            {s.videoModel && (
              <p className="text-xs text-neutral-600">Generado con {s.videoModel}</p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

// ── Paso 6: entrega ──────────────────────────────────────────────────────────

function EntregaStep({ data, seriesId }: { data: SceneSeriesState; seriesId: string }) {
  const build = useBuildFinal(seriesId);
  const final = data.job.finalVideoAssetId;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-neutral-200">Los clips, de a uno</h3>
        <ul className="flex flex-col gap-1 text-sm">
          {data.scenes.map((s) => (
            <li key={s.id} className="flex items-center gap-3">
              <span className="text-neutral-300">{s.title}</span>
              <a
                href={`${BASE}/assets/${s.videoAssetId}/download`}
                className="text-emerald-400 hover:underline"
              >
                bajar
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-neutral-200">Todo junto</h3>
        <button
          onClick={() => build.mutate(undefined)}
          disabled={build.isPending}
          className="self-start rounded bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
        >
          {build.isPending ? 'Armando…' : final ? 'Rehacer el video final' : 'Armar el video final'}
        </button>

        {final && (
          <>
            <video src={`${BASE}/assets/${final}/proxy`} controls className="max-w-3xl rounded" />
            <a href={`${BASE}/assets/${final}/download`} className="text-sm text-emerald-400 hover:underline">
              Bajar el video final
            </a>
          </>
        )}
      </div>
    </section>
  );
}
